import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, ProgressBar, Form, Card, Row, Col } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faArrowLeft, faArrowRight, faCheckCircle, faComment,
  faFile, faMicrophone, faVideo, faClipboard, faList, faEdit, faQuestion, faRedo
} from "@fortawesome/free-solid-svg-icons";
import Loader from "components/Loader";
import AttachmentSelection from "components/AttachmentSelection/AttachmentSelection";
import AttachmentPreview from "components/AttachmentPreview/AttachmentPreview";
import CourseService from "services/CourseService";
import { makeUrlsClickable } from "utils/helpers";

const TeamCoursePreview = () => {
  const { id, teamId } = useParams();
  const history = useHistory();
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [completedModules, setCompletedModules] = useState(() => {
    const saved = localStorage.getItem(`teamCoursePreview_completed_${id}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [completedLessons, setCompletedLessons] = useState(() => {
    const saved = localStorage.getItem(`teamCoursePreview_completedLessons_${id}`);
    return saved ? JSON.parse(saved) : [];
  });
  const [lastVisitedIndex] = useState(() => {
    const saved = localStorage.getItem(`teamCoursePreview_lastIndex_${id}`);
    return saved ? Number(saved) : 0;
  });
  const [activeModuleIndex, setActiveModuleIndex] = useState(lastVisitedIndex);
  const [activeLessonIndex, setActiveLessonIndex] = useState(-1);
  const [responseType, setResponseType] = useState("text");
  const [textResponse, setTextResponse] = useState("");
  const [responseAttachment, setResponseAttachment] = useState(null);
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [hasUserInteractedWithResponse, setHasUserInteractedWithResponse] = useState(false);
  const mainContentRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(`teamCoursePreview_completed_${id}`,
      JSON.stringify(completedModules));
  }, [completedModules, id]);

  useEffect(() => {
    localStorage.setItem(`teamCoursePreview_completedLessons_${id}`,
      JSON.stringify(completedLessons));
  }, [completedLessons, id]);

  useEffect(() => {
    localStorage.setItem(`teamCoursePreview_lastIndex_${id}`,
      String(activeModuleIndex));
  }, [activeModuleIndex, id]);

  useEffect(() => {
    setActiveModuleIndex(lastVisitedIndex);
    setActiveLessonIndex(-1);
  }, [course, lastVisitedIndex]);

  useEffect(() => {
    if (activeLessonIndex >= 0 && course && course.modules[activeModuleIndex] && course.modules[activeModuleIndex].lessons[activeLessonIndex]) {
      const lessonKey = `${activeModuleIndex}-${course.modules[activeModuleIndex].lessons[activeLessonIndex].id}`;
      const savedResponses = JSON.parse(sessionStorage.getItem('lessonResponses') || '{}');
      
      if (savedResponses[lessonKey]) {
        const savedData = savedResponses[lessonKey];
        setTextResponse(savedData.textResponse || "");
        setResponseType(savedData.responseType || "text");
        setHasUserInteractedWithResponse(savedData.hasUserInteractedWithResponse || false);
      }
    }
  }, [activeLessonIndex, activeModuleIndex, course]);
  
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        
        const response = await CourseService.getCourse(teamId, id);
        
        if (response && response.data) {
          setCourse(response.data.data);
        } else {
          throw new Error('Course not found');
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching course:", error);
        addToast("Failed to load course data", { appearance: "error" });
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id, teamId, addToast]);
  
  const scrollToTop = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const handleNextModule = () => {
    if (activeModuleIndex < course.modules.length - 1) {
      setActiveModuleIndex(activeModuleIndex + 1);
      setActiveLessonIndex(-1);
      setShowResponseForm(false);
      resetResponseData();
      scrollToTop();
    }
  };
  
  const handlePreviousModule = () => {
    if (activeModuleIndex > 0) {
      setActiveModuleIndex(activeModuleIndex - 1);
      setActiveLessonIndex(-1);
      setShowResponseForm(false);
      resetResponseData();
      scrollToTop();
    }
  };
  
  const handleGoToModule = (index) => {
    setActiveModuleIndex(index);
    setActiveLessonIndex(-1);
    setShowResponseForm(false)
    resetResponseData();
    scrollToTop();
  };

  const handleGoToLesson = (lessonIndex) => {
    if (activeLessonIndex >= 0) {
      const currentLessonKey = `${activeModuleIndex}-${activeModule.lessons[activeLessonIndex].id}`;
      const responseData = {
        textResponse,
        responseType,
        hasUserInteractedWithResponse,
        hasAttachment: !!responseAttachment
      };
      
      const savedResponses = JSON.parse(sessionStorage.getItem('lessonResponses') || '{}');
      savedResponses[currentLessonKey] = responseData;
      sessionStorage.setItem('lessonResponses', JSON.stringify(savedResponses));
    }

    setActiveLessonIndex(lessonIndex);
    setShowResponseForm(false);
    
    setTextResponse("");
    setResponseAttachment(null);
    setResponseType("text");
    setHasUserInteractedWithResponse(false);
    
    setTimeout(() => {
      if (course && course.modules[activeModuleIndex] && course.modules[activeModuleIndex].lessons[lessonIndex]) {
        const newLessonKey = `${activeModuleIndex}-${course.modules[activeModuleIndex].lessons[lessonIndex].id}`;
        const savedResponses = JSON.parse(sessionStorage.getItem('lessonResponses') || '{}');
        
        if (savedResponses[newLessonKey]) {
          const savedData = savedResponses[newLessonKey];
          setTextResponse(savedData.textResponse || "");
          setResponseType(savedData.responseType || "text");
          setHasUserInteractedWithResponse(savedData.hasUserInteractedWithResponse || false);
        }
      }
      
      scrollToTop();
    }, 0);
  };

  const handleBackToModuleOverview = () => {
    setActiveLessonIndex(-1);
    setShowResponseForm(false);
    resetResponseData();
    setTimeout(() => {
      scrollToTop();
    }, 0);
  };

  const resetResponseData = () => {
    if (activeLessonIndex >= 0) {
      const activeModule = course.modules[activeModuleIndex];
      const lessonKey = `${activeModuleIndex}-${activeModule.lessons[activeLessonIndex].id}`;
      const responseData = {
        textResponse,
        responseType,
        hasUserInteractedWithResponse,
        hasAttachment: !!responseAttachment
      };
      
      const savedResponses = JSON.parse(sessionStorage.getItem('lessonResponses') || '{}');
      savedResponses[lessonKey] = responseData;
      sessionStorage.setItem('lessonResponses', JSON.stringify(savedResponses));
    }
    
    setTextResponse("");
    setResponseAttachment(null);
    setResponseType("text");
    setHasUserInteractedWithResponse(false);
  };

  const handleMarkLessonAsCompleted = (lessonId) => {
    const lessonKey = `${activeModuleIndex}-${lessonId}`;
    
    if (completedLessons.includes(lessonKey)) {
      setCompletedLessons(completedLessons.filter(key => key !== lessonKey));
      
      if (completedModules.includes(activeModuleIndex)) {
        setCompletedModules(completedModules.filter(index => index !== activeModuleIndex));
      }
    } else {
      // Find the lesson index in the current module
      const activeModule = course.modules[activeModuleIndex];
      const lessonIndex = activeModule.lessons.findIndex(lesson => lesson.id === lessonId);
      
      // Check if previous lessons and modules are completed
      if (!canCompleteLesson(activeModuleIndex, lessonIndex)) {
        addToast("You must complete previous lessons first", { appearance: "error" });
        return;
      }
      
      setCompletedLessons([...completedLessons, lessonKey]);

      const updatedCompletedLessons = [...completedLessons, lessonKey];
      const allLessonsCompleted = activeModule.lessons.every((lesson) => {
        const key = `${activeModuleIndex}-${lesson.id}`;
        return updatedCompletedLessons.includes(key);
      });

      if (allLessonsCompleted && !completedModules.includes(activeModuleIndex)) {
        setCompletedModules([...completedModules, activeModuleIndex]);
      }
    }
  };

  const areAllLessonsInModuleCompleted = (moduleIndex) => {
    if (!course || !course.modules[moduleIndex] || !course.modules[moduleIndex].lessons) {
      return false;
    }
    
    const module = course.modules[moduleIndex];
    return module.lessons.every((lesson) => {
      const key = `${moduleIndex}-${lesson.id}`;
      return completedLessons.includes(key);
    });
  };

  const getCompletedLessonsInModuleCount = (moduleIndex) => {
    if (!course || !course.modules[moduleIndex] || !course.modules[moduleIndex].lessons) {
      return 0;
    }
    
    const module = course.modules[moduleIndex];
    return module.lessons.reduce((count, lesson) => {
      const key = `${moduleIndex}-${lesson.id}`;
      return completedLessons.includes(key) ? count + 1 : count;
    }, 0);
  };

  const handleFinish = () => {
    localStorage.removeItem(`teamCoursePreview_completed_${id}`);
    localStorage.removeItem(`teamCoursePreview_completedLessons_${id}`);
    localStorage.removeItem(`teamCoursePreview_lastIndex_${id}`);
    history.push(`/teams/${teamId}/coursebuilder/${id}/manage`);
  };

  const handleResetCompletion = () => {
    setCompletedModules([]);
    setCompletedLessons([]);
    localStorage.removeItem(`teamCoursePreview_completed_${id}`);
    localStorage.removeItem(`teamCoursePreview_completedLessons_${id}`);
    addToast("Course progress has been reset", { appearance: "success" });
  };

  const handleResponseTypeChange = (type) => {
    setResponseType(type);
    
    if (type === 'text') {
      setHasUserInteractedWithResponse(textResponse.trim() !== '');
    } else {
      setHasUserInteractedWithResponse(!!responseAttachment);
    }
    
    if (type !== 'text') {
      setTextResponse('');
    }
    if (responseAttachment && type !== 'file') {
      setResponseAttachment(null);
    }
  };

  const handleAttachmentSelected = (file) => {
    setResponseAttachment(file);
    setHasUserInteractedWithResponse(!!file);
  };

  const handleClearAttachment = () => {
    setResponseAttachment(null);
    if (responseType === 'text' ? !textResponse.trim() : true) {
      setHasUserInteractedWithResponse(false);
    }
  };

  const handleTextResponseChange = (e) => {
    setTextResponse(e.target.value);
    if (e.target.value.trim() !== '') {
      setHasUserInteractedWithResponse(true);
    } else {
      setHasUserInteractedWithResponse(false);
    }
  };

  const handleSubmitResponse = () => {
    // Check if the user can complete this lesson (previous lessons and modules are completed)
    if (activeLessonIndex >= 0 && !canCompleteLesson(activeModuleIndex, activeLessonIndex)) {
      addToast("You must complete previous lessons first", { appearance: "error" });
      return;
    }
    
    addToast("Response submitted successfully", { appearance: "success" });
    setShowResponseForm(false);
    
    setTextResponse("");
    setResponseAttachment(null);
    setResponseType("text");
    setHasUserInteractedWithResponse(false);
    
    if (activeLessonIndex >= 0) {
      const lessonKey = `${activeModuleIndex}-${activeModule.lessons[activeLessonIndex].id}`;
      const savedResponses = JSON.parse(sessionStorage.getItem('lessonResponses') || '{}');
      if (savedResponses[lessonKey]) {
        delete savedResponses[lessonKey];
        sessionStorage.setItem('lessonResponses', JSON.stringify(savedResponses));
      }
    }
    
    if (activeLessonIndex >= 0) {
      const activeModule = course.modules[activeModuleIndex];
      const activeLesson = activeModule.lessons[activeLessonIndex];
      handleMarkLessonAsCompleted(activeLesson.id);
    }
  };

  const isLessonCompleted = (moduleIndex, lessonId) => {
    return completedLessons.includes(`${moduleIndex}-${lessonId}`);
  };

  const isPreviousModulesCompleted = (moduleIndex) => {
    if (moduleIndex === 0) return true;
    
    for (let i = 0; i < moduleIndex; i++) {
      if (!areAllLessonsInModuleCompleted(i)) {
        return false;
      }
    }
    return true;
  };
  
  const isPreviousLessonsInModuleCompleted = (moduleIndex, lessonIndex) => {
    if (lessonIndex === 0) return true;
    
    const module = course.modules[moduleIndex];
    if (!module || !module.lessons) return true;
    
    for (let i = 0; i < lessonIndex; i++) {
      const lesson = module.lessons[i];
      if (!isLessonCompleted(moduleIndex, lesson.id)) {
        return false;
      }
    }
    return true;
  };
  
  const canCompleteLesson = (moduleIndex, lessonIndex) => {
    return isPreviousModulesCompleted(moduleIndex) && 
           isPreviousLessonsInModuleCompleted(moduleIndex, lessonIndex);
  };

  const getTotalLessonsCount = () => {
    if (!course || !course.modules) return 0;
    
    return course.modules.reduce((count, module) => {
      return count + (module.lessons ? module.lessons.length : 0);
    }, 0);
  };

  const getCompletedLessonsCount = () => {
    return completedLessons.length;
  };

  const progressPercent =
    course && course.modules.length > 0
      ? Math.round((getCompletedLessonsCount() / getTotalLessonsCount()) * 100) || 0
      : 0;
  
  if (loading || !course) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Loader />
      </div>
    );
  }

  const activeModule = course.modules[activeModuleIndex];
  const activeLesson = activeLessonIndex >= 0 && activeModule.lessons ? activeModule.lessons[activeLessonIndex] : null;
  
  return (
    <div className="d-flex flex-column user-course-view-style course-preview">
      <div className="container-fluid flex-grow-1" ref={mainContentRef}>
        <div className="py-4">
          <div className="mb-3">
            <Button 
              variant="link" 
              className="p-0 text-decoration-none" 
              onClick={() => history.goBack()}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Go Back
            </Button>
            <span className={`course-status-pill badge bg-warning ms-3`}>Preview Mode</span>
            <Button 
              variant="link" 
              className="ms-3 text-danger" 
              onClick={handleResetCompletion}
              disabled={completedLessons.length === 0}
              style={{ opacity: completedLessons.length === 0 ? 0.5 : 1 }}
            >
              <FontAwesomeIcon icon={faRedo} className="me-1" /> Reset Progress
            </Button>
          </div>

          <Row>
            <Col md={4}>
              <div className="sticky-left-column">
                {/* Course Info */}
                <Card className="mb-4">
                  <Card.Body>
                    <h4 className="mb-3 course-title">{course.title}</h4>
                    <p>{course.description}</p>

                    <div className="mt-4">
                      <div className="d-flex justify-content-between mb-1">
                        <span>Overall Progress</span>
                        <span>{progressPercent}% ({getCompletedLessonsCount()}/{getTotalLessonsCount()} lessons)</span>
                      </div>
                      <ProgressBar now={progressPercent} variant="success" />
                    </div>
                  </Card.Body>
                </Card>

                {/* Module List */}
                <div className="mb-4 modules-list">
                  {course.modules.map((module, moduleIdx) => (
                    <div key={module.id} className="mb-3">
                      <div 
                        className={`p-3 border rounded ${moduleIdx === activeModuleIndex ? 'bg-light' : ''}`} 
                        onClick={() => handleGoToModule(moduleIdx)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            {areAllLessonsInModuleCompleted(moduleIdx) ? (
                              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                            ) : (
                              <div className="rounded-circle border me-2"></div>
                            )}
                            <div>
                              <div className="fw-bold">{module.title}</div>
                              <div className="text-muted small">{getCompletedLessonsInModuleCount(moduleIdx)}/{module.lessons?.length || 0} lessons</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
            
            <Col md={8}>
              {activeLessonIndex === -1 ? (
                // Module Overview
                <Card className="mb-4 sticky-card">
                  <Card.Body>
                    <h3 className="mb-3">{activeModule.title}</h3>
                    <div dangerouslySetInnerHTML={{ __html: makeUrlsClickable(activeModule.description || '') }}></div>
                    
                    {/* Module lessons list */}
                    <div className="mt-4">
                      <h5 className="mb-3">Lessons</h5>
                      {activeModule.lessons && activeModule.lessons.map((lesson, lessonIdx) => (
                        <div key={lesson.id} className="mb-2">
                          <Button
                            variant="light"
                            className={`lesson-button ${isLessonCompleted(activeModuleIndex, lesson.id) ? 'text-success' : ''}`}
                            onClick={() => handleGoToLesson(lessonIdx)}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>
                                {isLessonCompleted(activeModuleIndex, lesson.id) ? (
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                ) : (
                                  <div className="rounded-circle border me-2 d-inline-block" style={{ width: '1rem', height: '1rem', position: 'relative', top: '2px' }}></div>
                                )}
                                {lesson.title || `Lesson ${lessonIdx + 1}`}
                                <div className="small text-muted ms-4 mt-1">
                                  <FontAwesomeIcon 
                                    icon={
                                      lesson.type === 'video' ? faVideo : 
                                      lesson.type === 'text' ? faClipboard :
                                      lesson.type === 'quiz' ? faQuestion :
                                      lesson.type === 'assignment' ? faEdit : 
                                      faFile
                                    } 
                                    className="me-1" 
                                    style={{ fontSize: '0.8rem' }}
                                  /> 
                                  {lesson.type === 'video' ? 'Video' :
                                    lesson.type === 'text' ? 'Text' :
                                    lesson.type === 'quiz' ? 'Quiz' :
                                    lesson.type === 'assignment' ? 'Assignment' : 
                                    'Document'}
                                </div>
                              </span>
                              <span className="lesson-duration">
                                <div className="lesson-duration">
                                  {lesson.duration || '5'} min
                                </div>
                              </span>
                            </div>
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Navigation buttons */}
                    <div className="button-container mt-4">
                      <Button
                        variant="outline-secondary"
                        className="responsive-button"
                        onClick={handlePreviousModule}
                        disabled={activeModuleIndex === 0}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Previous Module
                      </Button>
                      
                      {activeModuleIndex < course.modules.length - 1 ? (
                        <Button
                          variant="primary"
                          className="responsive-button ms-auto"
                          onClick={handleNextModule}
                        >
                          Next Module
                          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                        </Button>
                      ) : (
                        course.modules.every((_, index) => areAllLessonsInModuleCompleted(index)) ? (
                          <Button
                            variant="success"
                            className="responsive-button ms-auto"
                            onClick={handleFinish}
                          >
                            Finish Preview
                            <FontAwesomeIcon icon={faCheckCircle} className="ms-2" />
                          </Button>
                        ) : null
                      )}
                    </div>
                  </Card.Body>
                </Card>
              ) : (
                // Lesson Content
                <Card>
                  <Card.Body>
                    {/* Lesson Navigation Breadcrumb */}
                    <div className="mb-3">
                      <Button 
                        variant="link" 
                        className="p-0 text-decoration-none" 
                        onClick={handleBackToModuleOverview}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Module
                      </Button>
                    </div>
                    
                    <h3 className="mb-3">{activeLesson.title || `Lesson ${activeLessonIndex + 1}`}</h3>
                    
                    {/* Lesson Content - Don't show for video lessons */}
                    {activeLesson.type !== 'video' && (
                      <div dangerouslySetInnerHTML={{ __html: makeUrlsClickable(typeof activeLesson.content === 'string' ? activeLesson.content || '' : '') }} className="course-content"></div>
                    )}
                    
                    {/* Video Embed */}
                    {activeLesson.type === 'video' && (
                      <div className="video-container mb-4">
                        {activeLesson.content instanceof File ? (
                          // For uploaded videos (content as binary)
                          <video controls className="w-100">
                            <source 
                              src={URL.createObjectURL(activeLesson.content)} 
                              type={activeLesson.content.type || 'video/mp4'} 
                            />
                            Your browser does not support the video tag.
                          </video>
                        ) : typeof activeLesson.content === 'object' && activeLesson.content?.url ? (
                          // For server-uploaded videos returning an object with URL
                          <video controls className="w-100">
                            <source 
                              src={activeLesson.content.url} 
                              type={activeLesson.content.mimeType || 'video/mp4'} 
                            />
                            Your browser does not support the video tag.
                          </video>
                        ) : activeLesson.content && typeof activeLesson.content === 'string' && (
                          // For external videos (YouTube, Vimeo, etc.)
                          <iframe
                            src={activeLesson.content}
                            title={activeLesson.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ width: '100%', height: '100%' }}
                          ></iframe>
                        )}
                      </div>
                    )}
                    
                    {/* Attachments - Only show for non-video lessons */}
                    {activeLesson.type !== 'video' && activeLesson.attachments && activeLesson.attachments.length > 0 && (
                      <div className="mt-4">
                        <h5>Attachments</h5>
                        {activeLesson.attachments.map((attachment, idx) => (
                          <div key={idx} className="mb-3">
                            {attachment.type === 'video' && (
                              <div className="mb-3">
                                <h5>Video</h5>
                                <div className="video-container">
                                  <video controls width="100%">
                                    <source src={attachment.url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              </div>
                            )}
                            
                            {attachment.type === 'audio' && (
                              <div className="mb-3">
                                <h5>Audio</h5>
                                <audio controls className="w-100">
                                  <source src={attachment.url} type="audio/mp3" />
                                  Your browser does not support the audio tag.
                                </audio>
                              </div>
                            )}
                            
                            {attachment.type === 'image' && (
                              <div className="mb-3">
                                <h5>Image</h5>
                                <img 
                                  src={attachment.url} 
                                  alt={attachment.originalName || "Lesson attachment"}
                                  className="img-fluid rounded"
                                  style={{ maxHeight: '400px' }}
                                />
                              </div>
                            )}
                            
                            {attachment.type !== 'video' && attachment.type !== 'audio' && attachment.type !== 'image' && (
                              <div className="mb-3">
                                <h5>Attachment</h5>
                                <a 
                                  href={attachment.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-secondary"
                                >
                                  <FontAwesomeIcon icon={faFile} className="me-2" />
                                  {attachment.originalName || attachment.fileName || "Download attachment"}
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Instructions/CTA - always show if exists */}
                    {activeLesson.cta && (
                      <div className="cta-section">
                        <Card className="border-primary">
                          <Card.Header className="bg-primary text-white">
                            <h5>Call to Action</h5>
                          </Card.Header>
                          <Card.Body>
                            <div dangerouslySetInnerHTML={{ __html: makeUrlsClickable(activeLesson.cta) }}></div>
                          </Card.Body>
                        </Card>
                      </div>
                    )}

                    {/* Response section - directly show options */}
                    {!showResponseForm && !isLessonCompleted(activeModuleIndex, activeLesson.id) ? (
                      <div className="response-container bg-light p-4 rounded shadow-sm">
                        <h5 className="mb-4 border-bottom pb-2">Your Response</h5>
                        
                        {/* Response type selector */}
                        <div className="d-flex flex-wrap gap-2 mb-4">
                          <Button 
                            variant={responseType === 'text' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleResponseTypeChange('text')}
                            className="d-flex align-items-center response-type-btn"
                          >
                            <FontAwesomeIcon icon={faComment} className="me-2" />
                            Text
                          </Button>
                          <Button 
                            variant={responseType === 'audio' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleResponseTypeChange('audio')}
                            className="d-flex align-items-center response-type-btn"
                          >
                            <FontAwesomeIcon icon={faMicrophone} className="me-2" />
                            Audio
                          </Button>
                          <Button 
                            variant={responseType === 'video' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleResponseTypeChange('video')}
                            className="d-flex align-items-center response-type-btn"
                          >
                            <FontAwesomeIcon icon={faVideo} className="me-2" />
                            Video
                          </Button>
                          <Button 
                            variant={responseType === 'file' ? 'primary' : 'outline-secondary'}
                            onClick={() => handleResponseTypeChange('file')}
                            className="d-flex align-items-center response-type-btn"
                          >
                            <FontAwesomeIcon icon={faFile} className="me-2" />
                            File
                          </Button>
                        </div>
                        
                        {responseType === 'text' && (
                          <Form.Group className="mb-4">
                            <Form.Control 
                              as="textarea" 
                              rows={5} 
                              placeholder="Share your thoughts, reflections, or answers here..."
                              onChange={handleTextResponseChange}
                              value={textResponse}
                              className="response-textarea"
                            />
                          </Form.Group>
                        )}
                        
                        {(responseType === 'audio' || responseType === 'video' || responseType === 'file') && (
                          <div className="mb-4">
                            <Form.Label className="mb-2">Upload {responseType}</Form.Label>
                            <div className="d-flex align-items-center flex-wrap gap-2 upload-container p-3 border rounded bg-white">
                              <AttachmentSelection 
                                onChange={handleAttachmentSelected}
                                accepted={responseType === 'audio' ? ['.mp3', '.wav'] : 
                                          responseType === 'video' ? ['.mp4', '.webm'] : 
                                          ['.pdf', '.doc', '.docx', '.jpg', '.png']}
                                allowDocuments={responseType === 'file'}
                              />
                              {responseAttachment && (
                                <AttachmentPreview
                                  commentAttachmentPreview={"moduleAttachmentPreview"}
                                  attachment={responseAttachment}
                                  clearAttachment={handleClearAttachment}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="d-flex justify-content-end mt-4">
                          {hasUserInteractedWithResponse ? (
                            <Button 
                              variant="primary"
                              onClick={handleSubmitResponse}
                              disabled={(responseType === 'text' && !textResponse.trim()) || 
                                       ((responseType === 'audio' || responseType === 'video' || responseType === 'file') && !responseAttachment)}
                              className="px-4 py-2"
                            >
                              <>
                                Submit Response
                                <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                              </>
                            </Button>
                          ) : (
                            <Button
                              variant="success"
                              className="mark-complete-btn px-4 py-2"
                              onClick={() => handleMarkLessonAsCompleted(activeLesson.id)}
                              disabled={!canCompleteLesson(activeModuleIndex, activeLessonIndex)}
                            >
                              <>
                                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                Mark Complete
                              </>
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Lesson completed indicator */}
                    {isLessonCompleted(activeModuleIndex, activeLesson.id) && (
                      <div className="d-flex justify-content-center my-4">
                        <div className="lesson-completed-tag">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          <span>Lesson Complete!</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Lesson navigation */}
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => handleGoToLesson(Math.max(0, activeLessonIndex - 1))}
                        disabled={activeLessonIndex === 0}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Previous Lesson
                      </Button>

                      {activeLessonIndex === activeModule.lessons.length - 1 ? (
                        <Button
                          variant="primary"
                          onClick={handleBackToModuleOverview}
                        >
                          Back to Module
                          <FontAwesomeIcon icon={faList} className="ms-2" />
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={() => handleGoToLesson(activeLessonIndex + 1)}
                        >
                          Next Lesson
                          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default TeamCoursePreview;